import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { email } from '@repletoryreach/sdk-api';
import { Stack, Card, Group, Title, Text, Button, Skeleton } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { TextInput, Select } from '@repletoryreach/sdk-ui/inputs';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { notifyError, notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { useT } from '../../../../i18n/I18nProvider';
import { StatusPill, type PillTone } from '../../../../components/panel';
import { formatDate, formatDateTime, formatRelative } from '../../../../lib/format';
import { CopyValue } from './CopyValue';
import { emailErrorMessage } from '../errors';

const MUTED = 'var(--rr-color-text-muted)';
const TONE: Record<email.EmailDomain['status'], PillTone> = { pending: 'submitted', verified: 'approved', failed: 'rejected' };

/**
 * Sending from the business's own domain (4 October 2026).
 *
 * Add the domain, create the records it is given at the domain's DNS host,
 * press Check until the provider finds them, then choose the address on it to
 * send from. Until then, and whenever the chosen domain is removed, email goes
 * from the business's platform address, so nothing stops while DNS catches up.
 */
export function OwnDomainCard({ settings, canEdit }: { settings: email.EmailSettings; canEdit: boolean }) {
  const t = useT();
  const qc = useQueryClient();
  const [domain, setDomain] = useState('');
  const [domainError, setDomainError] = useState('');

  const domains = useQuery({ queryKey: ['email-domains'], queryFn: () => email.listDomains() });
  const rows = domains.data?.data ?? [];
  const verified = useMemo(() => rows.filter((d) => d.status === 'verified'), [rows]);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['email-domains'] });
    void qc.invalidateQueries({ queryKey: ['email-settings'] });
    void qc.invalidateQueries({ queryKey: ['email-status'] });
  };

  const add = useMutation({
    mutationFn: (name: string) => email.addDomain({ domain: name }),
    onSuccess: () => { setDomain(''); refresh(); notifySuccess(t('settings.email.domain.added'), t('settings.nav.email')); },
    onError: (e) => setDomainError(emailErrorMessage(t, e))
  });

  const check = useMutation({
    mutationFn: (d: email.EmailDomain) => email.checkDomain(d.id),
    onSuccess: (res) => {
      refresh();
      const d = res.data;
      if (d.status === 'verified') notifySuccess(t('settings.email.domain.checked.verified', { domain: d.domain }), t('settings.nav.email'));
      else notifyError(t('settings.email.domain.checked.pending', { domain: d.domain }), t('settings.nav.email'));
    },
    onError: (e) => notifyError(emailErrorMessage(t, e), t('settings.nav.email'))
  });

  const remove = useMutation({
    mutationFn: (d: email.EmailDomain) => email.removeDomain(d.id),
    onSuccess: () => { refresh(); notifySuccess(t('settings.email.domain.removed'), t('settings.nav.email')); },
    onError: (e) => notifyError(emailErrorMessage(t, e), t('settings.nav.email'))
  });

  function submitDomain() {
    // The same tidying the service does: no scheme, no www., no trailing slash.
    const name = domain.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/[/.]+$/, '').replace(/^www\./, '');
    if (!/^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(name)) {
      setDomainError(t('settings.email.domain.addInvalid'));
      return;
    }
    add.mutate(name);
  }

  function confirmRemove(d: email.EmailDomain) {
    openConfirmModal({
      title: t('settings.email.domain.removeTitle', { domain: d.domain }),
      children: <Text role="bodySmall">{t('settings.email.domain.removeBody', { domain: d.domain, address: settings.platform_address ?? '' })}</Text>,
      labels: { confirm: t('action.remove'), cancel: t('action.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: () => remove.mutate(d)
    });
  }

  return (
    <Card withBorder data-testid="email-own-domain">
      <Stack gap="md">
        <Stack gap="xs">
          <Title order={4}>{t('settings.email.domain.title')}</Title>
          <Text role="bodySmall" c={MUTED}>{t('settings.email.domain.intro')}</Text>
        </Stack>

        {domains.isPending && <Skeleton height={80} />}
        {domains.error && (
          <Alert color="error" role="alert">
            <Group justify="space-between" align="center" wrap="wrap">
              <Text role="bodySmall">{t('settings.email.domain.loadError')}</Text>
              <Button size="xs" variant="outline" onClick={() => { void domains.refetch(); }}>{t('action.retry')}</Button>
            </Group>
          </Alert>
        )}
        {!domains.isPending && !domains.error && rows.length === 0 && (
          <Text role="bodySmall" c={MUTED}>{t('settings.email.domain.empty')}</Text>
        )}

        {rows.map((d) => (
          <div key={d.id} className="rr-email__domain" data-testid="email-domain">
            <Group justify="space-between" align="flex-start" wrap="wrap" gap="sm">
              <Stack gap={2} style={{ minWidth: 0 }}>
                <Group gap="xs" wrap="wrap">
                  <Text role="body" fw={600}>{d.domain}</Text>
                  <StatusPill tone={TONE[d.status]}>{t(`settings.email.domain.status.${d.status}`)}</StatusPill>
                </Group>
                <Text role="caption" c={MUTED}>
                  {d.status === 'verified' && d.verified_at
                    ? t('settings.email.domain.verifiedOn', { when: formatDate(d.verified_at) })
                    : d.checked_at
                      ? <span title={formatDateTime(d.checked_at)}>{t('settings.email.domain.lastChecked', { when: formatRelative(d.checked_at) })}</span>
                      : t('settings.email.domain.notChecked')}
                </Text>
              </Stack>
              {canEdit && (
                <Group gap="xs" wrap="nowrap">
                  <Button
                    size="xs"
                    variant="default"
                    loading={check.isPending && check.variables?.id === d.id}
                    onClick={() => check.mutate(d)}
                  >
                    {t('settings.email.domain.check')}
                  </Button>
                  <Button size="xs" variant="subtle" color="red" onClick={() => confirmRemove(d)}>{t('action.remove')}</Button>
                </Group>
              )}
            </Group>

            {d.check_again && <Alert color="warning">{t('settings.email.domain.checkAgain')}</Alert>}
            {d.status === 'failed' && d.detail && <Text role="caption" c={MUTED}>{d.detail}</Text>}

            {d.status !== 'verified' && d.records.length > 0 && (
              <Stack gap="xs">
                <Text role="label">{t('settings.email.domain.records')}</Text>
                <Text role="caption" c={MUTED}>{t('settings.email.domain.recordsHint')}</Text>
                <div className="rr-email__records" role="table" aria-label={t('settings.email.domain.records')}>
                  <div className="rr-email__record rr-email__record--head" role="row">
                    <span role="columnheader">{t('settings.email.domain.col.type')}</span>
                    <span role="columnheader">{t('settings.email.domain.col.host')}</span>
                    <span role="columnheader">{t('settings.email.domain.col.value')}</span>
                  </div>
                  {d.records.map((r, i) => (
                    <div key={`${r.type}-${r.host}-${i}`} className="rr-email__record" role="row" data-testid="email-dns-record">
                      <span role="cell" className="rr-email__type">
                        <span className="rr-email__cell-label">{t('settings.email.domain.col.type')}</span>
                        <code className="rr-email__value">{r.type}</code>
                        {r.verified === true && <StatusPill tone="approved">{t('settings.email.domain.recordFound')}</StatusPill>}
                        {r.verified === false && <StatusPill tone="neutral">{t('settings.email.domain.recordMissing')}</StatusPill>}
                      </span>
                      <span role="cell">
                        <span className="rr-email__cell-label">{t('settings.email.domain.col.host')}</span>
                        <CopyValue value={r.host} label={t('settings.email.domain.col.host')} />
                      </span>
                      <span role="cell">
                        <span className="rr-email__cell-label">{t('settings.email.domain.col.value')}</span>
                        <CopyValue value={r.value} label={t('settings.email.domain.col.value')} />
                      </span>
                    </div>
                  ))}
                </div>
              </Stack>
            )}
          </div>
        ))}

        {canEdit && (
          <Group gap="sm" align="flex-start" wrap="wrap">
            <div style={{ flex: '1 1 260px', minWidth: 0 }}>
              <TextInput
                label={t('settings.email.domain.add')}
                description={t('settings.email.domain.addHint')}
                placeholder="yourbusiness.co.za"
                value={domain}
                error={domainError || undefined}
                onChange={(e) => { setDomain(e.currentTarget.value); setDomainError(''); }}
                onKeyDown={(e) => { if (e.key === 'Enter') submitDomain(); }}
              />
            </div>
            <Button style={{ marginTop: 26 }} loading={add.isPending} disabled={!domain.trim()} onClick={submitDomain}>
              {t('settings.email.domain.addButton')}
            </Button>
          </Group>
        )}

        <SendFrom settings={settings} verified={verified} canEdit={canEdit} onSaved={refresh} />
      </Stack>
    </Card>
  );
}

/** Which verified domain, and which address on it, the business's email goes from. */
function SendFrom({ settings, verified, canEdit, onSaved }: {
  settings: email.EmailSettings;
  verified: email.EmailDomain[];
  canEdit: boolean;
  onSaved: () => void;
}) {
  const t = useT();
  const usingOwn = !!settings.own_domain_id && verified.some((d) => d.id === settings.own_domain_id);
  const [domainId, setDomainId] = useState('');
  const [local, setLocal] = useState('');
  const [localError, setLocalError] = useState('');

  useEffect(() => {
    setDomainId(settings.own_domain_id && verified.some((d) => d.id === settings.own_domain_id) ? settings.own_domain_id : verified[0]?.id ?? '');
    setLocal(settings.own_local_part ?? '');
  }, [settings.own_domain_id, settings.own_local_part, verified]);

  const chosen = verified.find((d) => d.id === domainId);
  const address = chosen && local.trim() ? `${local.trim().toLowerCase()}@${chosen.domain}` : '';

  const save = useMutation({
    mutationFn: () => email.updateSettings({ use_own_domain: true, own_domain_id: domainId, own_local_part: local.trim().toLowerCase() }),
    onSuccess: (res) => { onSaved(); notifySuccess(t('settings.email.sendFrom.saved', { address: res.data.sending_from ?? address }), t('settings.nav.email')); },
    onError: (e) => setLocalError(emailErrorMessage(t, e))
  });
  const usePlatform = useMutation({
    mutationFn: () => email.updateSettings({ use_own_domain: false }),
    onSuccess: () => { onSaved(); notifySuccess(t('settings.email.sendFrom.platformSaved', { address: settings.platform_address ?? '' }), t('settings.nav.email')); },
    onError: (e) => notifyError(emailErrorMessage(t, e), t('settings.nav.email'))
  });

  function submit() {
    // The service's own rule for the part before the @.
    if (!/^[a-z0-9](?:[a-z0-9._-]{0,62}[a-z0-9])?$/.test(local.trim().toLowerCase())) {
      setLocalError(t('settings.email.sendFrom.localInvalid'));
      return;
    }
    save.mutate();
  }

  if (verified.length === 0) {
    return <Text role="caption" c={MUTED}>{t('settings.email.sendFrom.needVerified')}</Text>;
  }

  return (
    <Stack gap="sm" className="rr-email__send-from" data-testid="email-send-from">
      <Text role="label">{t('settings.email.sendFrom.title')}</Text>
      {usingOwn && settings.sending_from && (
        <Text role="bodySmall">{t('settings.email.sendFrom.current', { address: settings.sending_from })}</Text>
      )}
      {canEdit && (
        <>
          <Group gap="sm" align="flex-start" wrap="wrap">
            <div style={{ flex: '1 1 200px', minWidth: 0 }}>
              <TextInput
                label={t('settings.email.sendFrom.local')}
                description={t('settings.email.sendFrom.localHint')}
                placeholder={t('settings.email.sendFrom.localPlaceholder')}
                value={local}
                error={localError || undefined}
                onChange={(e) => { setLocal(e.currentTarget.value); setLocalError(''); }}
              />
            </div>
            <div style={{ flex: '1 1 200px', minWidth: 0 }}>
              <Select
                label={t('settings.email.sendFrom.domain')}
                value={domainId}
                onChange={(e) => setDomainId(e.currentTarget.value)}
                data={verified.map((d) => ({ value: d.id, label: `@${d.domain}` }))}
              />
            </div>
          </Group>
          {address && <Text role="caption" c={MUTED}>{t('settings.email.sendFrom.preview', { address })}</Text>}
          <Group gap="sm" wrap="wrap">
            <Button size="sm" loading={save.isPending} disabled={!domainId || !local.trim()} onClick={submit}>
              {t('settings.email.sendFrom.save')}
            </Button>
            {usingOwn && settings.platform_address && (
              <Button size="sm" variant="default" loading={usePlatform.isPending} onClick={() => usePlatform.mutate()}>
                {t('settings.email.sendFrom.usePlatform', { address: settings.platform_address })}
              </Button>
            )}
          </Group>
        </>
      )}
    </Stack>
  );
}
